import React, { useState, useCallback, useEffect, useRef } from 'react';
import { API } from '@/App';
import { Upload, Database, FileText, Grid3X3, BarChart3, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { parseAxiosError } from '@/utils/axiosErrors';
import { FieldLayoutModal } from './FieldLayoutModal';
import { EfficiencyComparator } from './EfficiencyComparator';
import MultiTraitRadar from './MultiTraitRadar';
import GeneticGainForm from './GeneticGainForm';
import { INLINE_SAMPLES, SAMPLE_BANNERS } from './sampleData';
import { MUTATION_INLINE_SAMPLES, MUTATION_SAMPLE_BANNERS } from './mutationSampleData';
import { DISEASE_INLINE_SAMPLES, DISEASE_SAMPLE_BANNERS } from './diseaseResistanceSampleData';
import { PHYSIO_INLINE_SAMPLES, PHYSIO_SAMPLE_BANNERS } from './physiologySampleData';
import { SOIL_INLINE_SAMPLES, SOIL_SAMPLE_BANNERS } from './soilSampleData';
// Re-export the now-extracted sub-components so existing imports
// (e.g. `import { ExportBar } from '@/components/DesignUploadCard'`)
// continue to work — Pattern 8 backwards-compat boundary.
import { ExportBar } from './design/ExportBar';
import { InsightPanel } from './design/InsightPanel';
import { DataFormatBox, ValidationMessages, DuplicateBanner } from './design/UploadCardParts';

export { ExportBar };

// `ExportBar` and `InsightPanel` were extracted to their own
// modules under `components/design/` in the Feb 2026 P2 refactor.
// They are re-exported above so legacy imports keep working without
// touching downstream consumers.


export function DesignUploadCard({ design, onResult, analysisId, exporting, onExport }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [loadingSample, setLoadingSample] = useState(false);
  const [showLayout, setShowLayout] = useState(false);
  const [preview, setPreview] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  // Sub-Batch 3R-tail: row-level validation issues (row #, column, value, rule)
  const [rowLevelIssues, setRowLevelIssues] = useState([]);
  const [radarData, setRadarData] = useState(null);
  // P2 Niranjan (Feb 2026): when the user came from the Clean module
  // with a dataset that doesn't match this design's required schema,
  // we surface an explanatory banner instead of silently auto-seeding
  // the file. The marker is written by BiometricalAnalysis's handoff
  // useEffect; we read it on mount and clear it once the user picks
  // a fresh file or loads sample data.
  const [schemaMismatch, setSchemaMismatch] = useState(null);
  // Module-specific config state
  const [pooledStructure, setPooledStructure] = useState(() => {
    try {
      const s = sessionStorage.getItem('bk_advisor_structure');
      if (s) { sessionStorage.removeItem('bk_advisor_structure'); return s; }
    } catch {}
    return 'locations_one_year';
  });
  const [metIncludeLoc, setMetIncludeLoc] = useState(true);
  const [metIncludeYear, setMetIncludeYear] = useState(true);
  const [metIncludeLY, setMetIncludeLY] = useState(true);
  // Heterosis config (only used by diallel & L×T endpoints) —
  // standard check value for SH calculation, and direction toggle.
  // Niranjan, Feb 2026 — Phase A heterosis integration.
  const [stdCheck, setStdCheck] = useState('');
  const [higherIsBetter, setHigherIsBetter] = useState(true);
  const [parentMeansInput, setParentMeansInput] = useState('');
  const supportsHeterosis = (
    design.family === 'diallel' || design.family === 'lxt'
  );

  // ── Sub-Batch 3R: Upload UX hardening ───────────────────────────────────
  // Progress / elapsed / structured-failure / idempotency wiring.
  const [uploadProgress, setUploadProgress] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [forceRerun, setForceRerun] = useState(false);
  // structured failure: { stage, detail, hint }
  const [runError, setRunError] = useState(null);
  const elapsedTimerRef = useRef(null);
  const uploadCancelRef = useRef(null);
  const analysisAbortRef = useRef(null);
  // Sub-Batch 3R-tail (L1g) — opt-in email-on-completion checkbox.
  const [notifyOnCompletion, setNotifyOnCompletion] = useState(false);

  // Start / stop / cleanup the elapsed-time counter
  useEffect(() => {
    if (uploading) {
      setElapsedSec(0);
      elapsedTimerRef.current = setInterval(
        () => setElapsedSec((s) => s + 1), 1000,
      );
    } else if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    return () => {
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
    };
  }, [uploading]);

  // Idempotency check — called from selectFile after validation succeeds.
  // Hits POST /api/uploads/check-duplicate which returns a list of
  // identical-hash analyses from the past 24h.  We expose the result so
  // the user can pick "View Previous Result" or "Force Re-Run".
  const checkDuplicate = async (selectedFile) => {
    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      // Filter dupes to the SAME analysis module so RBD vs CRD of the
      // same CSV doesn't trigger a false positive.
      const at = design.analysisType || design.endpoint?.replace('/analyze/', '') || '';
      if (at) fd.append('analysis_type', at);
      const { data } = await axios.post(
        `${API}/uploads/check-duplicate`, fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (data?.within_24h && (data.duplicates || []).length > 0) {
        setDuplicateInfo(data);
      } else {
        setDuplicateInfo(null);
      }
    } catch (_) {
      // Fail-open: idempotency check is a UX nicety, never block upload.
      setDuplicateInfo(null);
    }
  };

  // Cancel handlers for active upload / running analysis
  const cancelAnalysis = () => {
    if (analysisAbortRef.current) {
      analysisAbortRef.current.abort();
      analysisAbortRef.current = null;
    }
    setUploading(false);
    setUploadProgress(0);
    setRunError({
      stage: 'cancelled',
      detail: 'Analysis cancelled by user.',
      hint: (
        'Note: if the analysis was already saving to the database when '
        + 'you cancelled, the partial result may still appear on your '
        + 'Dashboard (mid-persistence cancels are graceful no-ops).'
      ),
    });
  };

  // Prefill from sessionStorage if another analysis handed off a file.
  // Also read the schema-mismatch marker so the card can surface a
  // friendly banner when the cleaned dataset shape doesn't match the
  // design's required structural columns.
  useEffect(() => {
    try {
      const handoff = sessionStorage.getItem(`bk_handoff_${design.key}`);
      if (handoff) {
        const { csv, filename } = JSON.parse(handoff);
        const blob = new Blob([csv], { type: 'text/csv' });
        const f = new File([blob], filename || `handoff_${design.key}.csv`, { type: 'text/csv' });
        selectFile(f);
        sessionStorage.removeItem(`bk_handoff_${design.key}`);
        toast.info('Data carried over from previous analysis — click Run to analyze.');
      }
    } catch {}
    try {
      const mismatchRaw = sessionStorage.getItem(`bk_schema_mismatch_${design.key}`);
      if (mismatchRaw) {
        const m = JSON.parse(mismatchRaw);
        setSchemaMismatch(m);
      }
    } catch {}
    // GenerationMeanLanding sample-card → auto-fetch the chosen sample
    // by exact key (gma vs gma-tobacco) and pre-fill the file input.
    // Niranjan, Feb 2026.
    try {
      const genmeanSampleKey = sessionStorage.getItem(`bk_genmean_sample_${design.key}`);
      if (genmeanSampleKey) {
        sessionStorage.removeItem(`bk_genmean_sample_${design.key}`);
        (async () => {
          try {
            const resp = await axios.get(`${API}/sample-data/${genmeanSampleKey}`,
              { responseType: 'text' });
            const csvText = typeof resp.data === 'string' ? resp.data : await resp.data.text();
            const blob = new Blob([csvText], { type: 'text/csv' });
            const file = new File([blob], `sample_${genmeanSampleKey}.csv`, { type: 'text/csv' });
            selectFile(file);
            toast.success('Sample data loaded. Click Run to analyse.');
          } catch (e) {
            toast.error('Could not load this sample.');
          }
        })();
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [design.key]);

  // Fetch radar data when analysis completes
  useEffect(() => {
    if (!analysisId) { setRadarData(null); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/analysis/${analysisId}`, { withCredentials: true });
        const results = data?.results || {};
        // Find per_trait_results for multi-trait analyses
        for (const [, section] of Object.entries(results)) {
          if (section?.per_trait_results) {
            const ptr = section.per_trait_results;
            const traits = Object.keys(ptr);
            if (traits.length >= 3) {
              const treatmentMeans = {};
              for (const t of traits) {
                treatmentMeans[t] = ptr[t]?.treatment_means || {};
              }
              setRadarData({ treatmentMeans, traits });
            }
            return;
          }
        }
        setRadarData(null);
      } catch { setRadarData(null); }
    })();
  }, [analysisId]);

  const validateFile = async (selectedFile) => {
    setValidating(true);
    setPreview(null);
    setValidationErrors([]);
    setValidationWarnings([]);
    setRowLevelIssues([]);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const { data } = await axios.post(`${API}/validate-upload`, formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(data.preview);
      setValidationErrors(data.errors || []);
      setValidationWarnings(data.warnings || []);
      setRowLevelIssues(data.row_level_issues || []);
      if (!data.valid) toast.error('File has issues — check the validation messages below.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not validate file');
      setValidationErrors([e.response?.data?.detail || 'Validation failed']);
    }
    setValidating(false);
  };

  const selectFile = (f) => {
    if (f) {
      setFile(f);
      setDuplicateInfo(null);
      setForceRerun(false);
      setRunError(null);
      validateFile(f);
      checkDuplicate(f);
      // The user just picked a fresh file — clear the schema-mismatch
      // banner from the clean-module handoff so it doesn't linger.
      setSchemaMismatch(null);
      try { sessionStorage.removeItem(`bk_schema_mismatch_${design.key}`); } catch {}
    }
  };

  // Qualitative/DUS crop picker → push a chosen crop's demo CSV into this
  // card without a remount. The landing dispatches a window event carrying
  // the CSV; we load it only if it targets this design.
  useEffect(() => {
    const onLoadSample = (e) => {
      const d = e.detail || {};
      if (d.designKey !== design.key || !d.csv) return;
      const blob = new Blob([d.csv], { type: 'text/csv' });
      const f = new File([blob], d.filename || `sample_${design.key}.csv`, { type: 'text/csv' });
      selectFile(f);
      toast.success(d.banner || 'Sample data loaded! Click Run to analyse.');
      const el = document.querySelector('[data-testid^="upload-zone-"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    window.addEventListener('bk-load-dus-sample', onLoadSample);
    return () => window.removeEventListener('bk-load-dus-sample', onLoadSample);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [design.key]);


  const handleDrag = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f && f.name.match(/\.(csv|xlsx|xls)$/i)) selectFile(f);
    else toast.error('Upload CSV or Excel files only');
  }, []);

  const handleUpload = async () => {
    if (!file) return toast.error('Select a file first');
    // Sub-Batch 3R: idempotency gate.  If we found a duplicate and
    // the user hasn't explicitly clicked "Force Re-Run", refuse to
    // submit and re-show the banner.
    if (duplicateInfo && !forceRerun) {
      toast.warning('This file was already analyzed in the past 24h. Use the banner buttons.');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setRunError(null);
    const formData = new FormData();
    formData.append('file', file);
    if (design.methodValue) formData.append('method', design.methodValue);
    if (design.key === 'pooled-anova') formData.append('structure', pooledStructure);
    if (design.key === 'full-met') {
      formData.append('include_location', String(metIncludeLoc));
      formData.append('include_year', String(metIncludeYear));
      formData.append('include_ly_interaction', String(metIncludeLY));
    }
    if (supportsHeterosis) {
      formData.append('higher_is_better', String(higherIsBetter));
      const sc = parseFloat(stdCheck);
      if (!Number.isNaN(sc)) formData.append('standard_check', String(sc));
      const trimmedPM = (parentMeansInput || '').trim();
      if (trimmedPM) formData.append('parent_means_input', trimmedPM);
    }
    // Abortable upload + analyze.  AbortController is the canonical
    // axios-compatible cancellation API in 2026.
    const controller = new AbortController();
    analysisAbortRef.current = controller;
    try {
      const { data } = await axios.post(`${API}${design.endpoint}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: controller.signal,
        onUploadProgress: (evt) => {
          if (evt.total) {
            setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
          }
        },
      });
      toast.success(`${design.label} analysis complete!`);
      setForceRerun(false);
      setDuplicateInfo(null);
      // Sub-Batch 3R-tail (L1g): fire-and-forget email-on-completion.
      // Backend bails cheaply if elapsedSec < 90, so this is safe to
      // call unconditionally when the user opted in.
      if (notifyOnCompletion && data?.id) {
        try {
          axios.post(
            `${API}/uploads/notify-completion`,
            {
              analysis_id: data.id,
              analysis_label: design.label,
              elapsed_sec: elapsedSec,
            },
            { withCredentials: true },
          ).then((r) => {
            if (r?.data?.sent) toast.info('We will email you the result link.');
          }).catch(() => { /* fail silently — user has the result tab */ });
        } catch { /* noop */ }
      }
      onResult(data);
    } catch (e) {
      // Structured failure surfacing: classify by stage so the user sees
      // what went wrong AND what to do next.
      if (axios.isCancel(e) || e.name === 'CanceledError' || e.name === 'AbortError') {
        // already handled by cancelAnalysis()
        return;
      }
      const status = e.response?.status || 0;
      // Free-plan quota (402) is surfaced globally by the axios interceptor
      // (upgrade toast + /pricing action). Don't show the scary "computation
      // failed" panel or notify admin for a paywall block — just reset state.
      if (status === 402) {
        return;  // finally{} still clears the uploading/progress state
      }
      const detail = e.response?.data?.detail
                     || parseAxiosError(e)
                     || e.message
                     || 'Unknown failure';
      let stage = 'computation';
      let hint = '';
      if (status === 0) {
        stage = 'network';
        hint = 'Check your internet connection and try again.';
      } else if (status === 401 || status === 403) {
        stage = 'auth';
        hint = 'Your session may have expired. Reload the page and log in again.';
      } else if (status === 413) {
        stage = 'upload';
        hint = 'File is too large for the server. Try a smaller subset or contact support.';
      } else if (/parse|read|csv|excel|column/i.test(detail)) {
        stage = 'parsing';
        hint = 'See the Data Format spec for this module (sidebar → Download Data Format) and check your column headers.';
      } else if (/validation|required|missing/i.test(detail)) {
        stage = 'validation';
        hint = 'See the validation errors above the Run Analysis button.';
      } else if (/insert|persist|mongo|database/i.test(detail)) {
        stage = 'persistence';
        hint = 'The analysis ran but could not be saved. Contact support with the timestamp.';
      } else {
        hint = 'Retry once; if it persists, share the file with support@bkagrid.com.';
      }
      setRunError({ stage, detail, hint });
      // Sub-Batch 3R-tail-3 (Dr. Kumar's direct ask): fire-and-forget
      // notification to admin so Dr. Kumar receives every failed run
      // and can intervene manually for the user.  Never blocks the UI.
      // Sub-Batch 3X (Jun 2026): capture the returned failure_id so the
      // "Notify me when fixed" checkbox can PATCH back to the same row.
      try {
        const resp = await axios.post(
          `${API}/uploads/notify-failure`,
          {
            design_label: design.label,
            filename: file?.name || '',
            stage,
            detail: String(detail).slice(0, 2000),
            file_sha256: duplicateInfo?.sha256 || null,
            notify_when_fixed: false,
          },
          { withCredentials: true },
        );
        if (resp?.data?.failure_id) {
          setRunError({ stage, detail, hint, failure_id: resp.data.failure_id });
        }
      } catch { /* fail silently — user has the banner */ }
      toast.error(`${stage.toUpperCase()} failed`, { description: detail.slice(0, 160) });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      analysisAbortRef.current = null;
    }
  };

  const loadSampleData = async () => {
    setLoadingSample(true);
    try {
      // The sample-data key matches `design.key` directly now that
      // Method II / IV have their own sidebar entries (no internal
      // method picker to translate).
      const sampleKey = design.key;

      // Helper: extract any "Parent Means: P1=42, ..." comment line
      // from a sample CSV and pre-fill the heterosis input. Lets users
      // run Method III/IV/L×T samples in one click without re-typing.
      const extractParentMeansHint = (csvText) => {
        const m = csvText.match(/#\s*For MPH\/BPH[^\n]*Parent Means:\s*(.+)/i);
        if (m && m[1]) setParentMeansInput(m[1].trim());
      };

      // Check inline sample data first (no backend round-trip)
      const inlineCSV = INLINE_SAMPLES[sampleKey] || MUTATION_INLINE_SAMPLES[sampleKey] || DISEASE_INLINE_SAMPLES[sampleKey] || PHYSIO_INLINE_SAMPLES[sampleKey] || SOIL_INLINE_SAMPLES[sampleKey];
      if (inlineCSV) {
        extractParentMeansHint(inlineCSV);
        const blob = new Blob([inlineCSV.trim()], { type: 'text/csv' });
        const sampleFile = new File([blob], `sample_${sampleKey}.csv`, { type: 'text/csv' });
        selectFile(sampleFile);
        toast.success(SAMPLE_BANNERS[sampleKey] || MUTATION_SAMPLE_BANNERS[sampleKey] || DISEASE_SAMPLE_BANNERS[sampleKey] || PHYSIO_SAMPLE_BANNERS[sampleKey] || SOIL_SAMPLE_BANNERS[sampleKey] || 'Sample data loaded! Click Run to analyze.');
        setLoadingSample(false);
        return;
      }

      // Fall back to backend for designs without inline data
      const response = await axios.get(`${API}/sample-data/${sampleKey}`, { responseType: 'text' });
      const csvText = typeof response.data === 'string' ? response.data : await response.data.text();
      extractParentMeansHint(csvText);
      const blob = new Blob([csvText], { type: 'text/csv' });
      const sampleFile = new File([blob], `sample_${sampleKey}.csv`, { type: 'text/csv' });
      selectFile(sampleFile);
      toast.success('Sample data loaded! Click Run to analyze.');
    } catch (err) {
      // B-NEW-2 fix: blob-typed responses can carry a JSON `detail`
      // body on 4xx/5xx — read it via .text() instead of letting
      // an uncaught TypeError bubble out of the bundle.
      const detail = await parseAxiosError(err, 'Sample data not available for this analysis.');
      toast.error(detail);
    }
    setLoadingSample(false);
  };

  const connectToPaper = (aid) => {
    window.location.href = `/bk-intelligence?tab=write-paper&analysis=${aid}`;
  };

  // Format a column list for the schema-mismatch banner. Long lists
  // (≥ 5 cols) are summarised as `[Col1, Col2, N traits]` so the
  // message stays readable on high-trait-count datasets.
  const _formatColumnSummary = (cols) => {
    if (!cols || cols.length === 0) return '[empty]';
    if (cols.length <= 4) return `[${cols.join(', ')}]`;
    // Heuristic: first 1-2 cols are usually identifiers (Genotype, Rep);
    // the rest are traits.
    const idHints = ['genotype', 'rep', 'replication', 'block', 'env',
      'environment', 'plot', 'entry', 'treatment', 'cross', 'parent',
      'line', 'tester', 'male', 'female', 'set', 'generation', 'year', 'location'];
    const isId = (c) => idHints.some(h => c.toLowerCase().includes(h));
    const ids = cols.filter(isId);
    const traits = cols.filter(c => !isId(c));
    if (ids.length > 0 && traits.length > 0) {
      const traitWord = traits.length === 1 ? 'trait' : 'traits';
      return `[${ids.join(', ')}, ${traits.length} ${traitWord}]`;
    }
    return `[${cols.slice(0, 3).join(', ')}, +${cols.length - 3} more]`;
  };

  return (
    <div className="space-y-4">
      {/* Data Format Box */}
      <DataFormatBox design={design} onTrySample={loadSampleData} loadingSample={loadingSample} />

      {/* Schema-mismatch banner — clean-module handoff with incompatible
          structure. P2 Niranjan (Feb 2026): D2_sanjana.xlsx is a 1181-row
          Genotype-only dataset; clicking a Mating Design card used to
          silently auto-seed it. Now we explain why the file isn't
          appropriate and direct the user toward Try Sample Data or a
          fresh upload with the right structural columns. */}
      {schemaMismatch && (
        <div
          className="rounded-lg p-4 fade-in"
          style={{ border: '1.5px solid #FBBF77', background: '#FFF7E6' }}
          data-testid={`schema-mismatch-${design.key}`}
        >
          <p className="font-body" style={{ fontSize: 13, color: '#9A4A02', fontWeight: 700, marginBottom: 6 }}>
            Cleaned dataset doesn't match this design's schema
          </p>
          <p className="font-body" style={{ fontSize: 13, color: '#5C3500', lineHeight: 1.5 }}>
            This design requires{' '}
            <span className="font-mono" style={{ fontWeight: 600 }}>[{schemaMismatch.required.join(', ')}, Trait]</span>.
            {schemaMismatch.altRequired && (
              <>
                {' '}(or{' '}
                <span className="font-mono" style={{ fontWeight: 600 }}>[{schemaMismatch.altRequired.join(', ')}, Trait]</span>).
              </>
            )}
            {' '}Your cleaned data ({schemaMismatch.filename}) has{' '}
            <span className="font-mono" style={{ fontWeight: 600 }}>{_formatColumnSummary(schemaMismatch.cleanedColumns)}</span>.
            {' '}Upload a file with cross identifiers to use this analysis,
            or click <strong>Try Sample Data</strong> above to see what a
            valid dataset looks like.
          </p>
        </div>
      )}

      {/* Pooled ANOVA structure radio */}
      {design.key === 'pooled-anova' && (
        <div className="space-y-2" data-testid="pooled-structure-selector">
          <label className="font-body" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-dark)' }}>Experimental structure:</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              { v: 'locations_one_year', l: 'Multiple Locations', d: 'Same year, multiple sites' },
              { v: 'years_one_location', l: 'Multiple Years', d: 'Same location over years' },
              { v: 'locations_and_years', l: 'Locations × Years', d: 'Full factorial' },
            ].map(opt => (
              <button
                key={opt.v}
                onClick={() => setPooledStructure(opt.v)}
                className="text-left px-3 py-2.5 rounded-lg transition-all font-body"
                style={{
                  fontSize: 13,
                  border: `2px solid ${pooledStructure === opt.v ? '#059669' : 'var(--border-color)'}`,
                  background: pooledStructure === opt.v ? '#05966910' : 'white',
                  color: pooledStructure === opt.v ? '#059669' : 'var(--text-medium)',
                }}
                data-testid={`pooled-structure-${opt.v}`}
              >
                <span style={{ fontWeight: 700 }}>{opt.l}</span>
                <span className="block mt-0.5" style={{ fontSize: 11, opacity: 0.8 }}>{opt.d}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Full MET toggles */}
      {design.key === 'full-met' && (
        <div className="space-y-2" data-testid="full-met-config">
          <label className="font-body" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-dark)' }}>ANOVA model:</label>
          <div className="flex flex-wrap gap-3">
            {[
              { k: 'loc', v: metIncludeLoc, set: setMetIncludeLoc, l: 'Include Location' },
              { k: 'year', v: metIncludeYear, set: setMetIncludeYear, l: 'Include Year' },
              { k: 'ly', v: metIncludeLY, set: setMetIncludeLY, l: 'Include Location × Year' },
            ].map(t => (
              <label key={t.k} className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition"
                style={{ border: `2px solid ${t.v ? '#059669' : 'var(--border-color)'}`, background: t.v ? '#05966910' : 'white' }}
                data-testid={`met-toggle-${t.k}`}>
                <input type="checkbox" checked={t.v} onChange={e => t.set(e.target.checked)} className="accent-emerald-700" />
                <span className="font-body" style={{ fontSize: 13, fontWeight: 600, color: t.v ? '#059669' : 'var(--text-medium)' }}>{t.l}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Heterosis config — only for diallel & L×T endpoints.
          Optional standard check + direction toggle. Niranjan, Feb 2026. */}
      {supportsHeterosis && (
        <div
          className="rounded-xl space-y-3"
          style={{
            border: '1.5px solid var(--border-color)',
            background: '#FAFCFA',
            padding: 16,
          }}
          data-testid="heterosis-config"
        >
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <span
              className="font-body"
              style={{ fontSize: 12, fontWeight: 700, color: 'var(--green-900)', textTransform: 'uppercase', letterSpacing: '0.06em' }}
            >
              Heterosis options (MPH · BPH · SH)
            </span>
            <span className="font-body" style={{ fontSize: 11, color: 'var(--text-light)', fontStyle: 'italic' }}>
              Optional — leave blank to use the best parent as the check
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label
                htmlFor={`stdcheck-${design.key}`}
                className="font-body block"
                style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-medium)', marginBottom: 4, letterSpacing: '0.02em' }}
              >
                STANDARD CHECK MEAN (trait units)
              </label>
              <input
                id={`stdcheck-${design.key}`}
                data-testid={`heterosis-stdcheck-${design.key}`}
                type="number"
                step="0.01"
                value={stdCheck}
                onChange={(e) => setStdCheck(e.target.value)}
                placeholder="e.g. 50.0"
                className="font-mono"
                style={{
                  width: '100%', fontSize: 13, padding: '7px 10px',
                  border: '1.5px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)', background: 'white',
                }}
              />
            </div>

            <div>
              <label
                className="font-body block"
                style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-medium)', marginBottom: 4, letterSpacing: '0.02em' }}
              >
                TRAIT DIRECTION
              </label>
              <div className="flex gap-2" data-testid={`heterosis-direction-${design.key}`}>
                {[
                  { v: true, l: 'Higher = better' },
                  { v: false, l: 'Lower = better' },
                ].map((opt) => (
                  <button
                    key={String(opt.v)}
                    type="button"
                    onClick={() => setHigherIsBetter(opt.v)}
                    className="flex-1 px-3 py-1.5 rounded-md font-body transition-all"
                    style={{
                      fontSize: 12, fontWeight: 600,
                      border: `1.5px solid ${higherIsBetter === opt.v ? 'var(--green-700)' : 'var(--border-color)'}`,
                      background: higherIsBetter === opt.v ? 'var(--green-50)' : 'white',
                      color: higherIsBetter === opt.v ? 'var(--green-900)' : 'var(--text-medium)',
                    }}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label
              htmlFor={`pmeans-${design.key}`}
              className="font-body block"
              style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-medium)', marginBottom: 4, letterSpacing: '0.02em' }}
            >
              PARENT MEANS (for MPH / BPH — comma-separated)
            </label>
            <input
              id={`pmeans-${design.key}`}
              data-testid={`heterosis-parent-means-${design.key}`}
              type="text"
              value={parentMeansInput}
              onChange={(e) => setParentMeansInput(e.target.value)}
              placeholder="e.g. P1=42, P2=55, P3=38, P4=60"
              className="font-mono"
              style={{
                width: '100%', fontSize: 13, padding: '7px 10px',
                border: '1.5px solid var(--border-color)',
                borderRadius: 'var(--radius-md)', background: 'white',
              }}
            />
            <p
              className="font-body"
              style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 4, fontStyle: 'italic' }}
            >
              Leave blank if your data already includes selfed parents (Method I/II).
              Required for Methods III/IV and L×T to compute MPH and BPH.
            </p>
          </div>
        </div>
      )}

      {/* Manual-entry calculator for designs that don't take a file
          upload (currently: Genetic Gain). Falconer & Mackay (1996). */}
      {design.noUpload && design.key === 'genetic-gain' && (
        <GeneticGainForm
          color={design.color}
          onResult={onResult}
        />
      )}

      {/* Upload Zone */}
      {!design.noUpload && (
        <>
          <div
            className="relative rounded-xl text-center cursor-pointer transition-all"
            style={{
              border: `2px dashed ${dragActive ? 'var(--green-900)' : '#CBD5E0'}`,
              padding: 48,
              background: dragActive ? '#E6F4EA' : '#FAFAFA',
              borderRadius: 'var(--radius-lg)',
            }}
            onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
            onClick={() => document.getElementById(`file-${design.key}`).click()}
            data-testid={`upload-area-${design.key}`}
          >
            <input id={`file-${design.key}`} type="file" className="hidden" accept=".csv,.xlsx,.xls"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) selectFile(f); }} />
            <Upload size={28} className="mx-auto mb-2" style={{ color: '#A0AEC0' }} />
            <p className="font-body" style={{ fontSize: 15, color: 'var(--text-medium)' }}>
              {file ? <span style={{ color: 'var(--green-700)', fontWeight: 600 }}>{file.name}</span> : 'Drop CSV/Excel here or click to browse'}
            </p>
          </div>

          {/* Validation Messages */}
          <ValidationMessages errors={validationErrors} warnings={validationWarnings} />

          {/* L1f — Row-level validation issues (Sub-Batch 3R-tail).
              Each entry points to a specific row + column + value +
              rule that was violated, so the user can fix the source
              file precisely instead of guessing. */}
          {rowLevelIssues.length > 0 && (
            <div
              className="rounded-lg p-3 mb-3 fade-in"
              style={{ border: '1px solid #F59E0B', background: '#FFFBEB' }}
              data-testid="row-level-issues-panel"
            >
              <div className="font-body mb-1 flex items-center gap-2"
                   style={{ fontSize: 12, fontWeight: 700, color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Row-level issues ({rowLevelIssues.length}{rowLevelIssues.length >= 10 ? '+' : ''} shown)
              </div>
              <ul className="font-body space-y-1" style={{ fontSize: 12, color: '#92400E' }}>
                {rowLevelIssues.map((issue, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`row-level-issue-${i}`}>
                    <span style={{ fontWeight: 700, fontSize: 11 }} className="shrink-0 mt-px">→</span>
                    <span>
                      Row <span className="font-mono font-semibold">{issue.row}</span>
                      , column <span className="font-mono font-semibold">{issue.column}</span>
                      : value <span className="font-mono">"{issue.value}"</span>
                      {issue.rule ? <> — <span className="italic">{issue.rule}</span></> : null}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* L1b — Parsing-stage indicator (Sub-Batch 3R-tail).  Distinct
              "Validating format..." block between file-drop and
              preview-ready / errors-ready.  Even on sub-second files,
              the indicator confirms to the user that the platform
              received the upload and is processing it. */}
          {validating && (
            <div
              className="rounded-lg p-4 mb-4 flex items-center gap-3"
              style={{
                background: 'rgba(45,106,79,0.08)',
                border: '1px solid var(--green-500)',
              }}
              data-testid="validating-format-indicator"
            >
              <Loader2 size={20} className="animate-spin" style={{ color: 'var(--green-700)' }} />
              <div className="flex-1 min-w-0">
                <div className="font-body" style={{ fontSize: 13, fontWeight: 600, color: 'var(--green-900)' }}>
                  Validating format...
                </div>
                <div className="font-body" style={{ fontSize: 11, color: 'var(--text-medium)' }}>
                  Checking your file against the {design.label} data-format spec.
                </div>
              </div>
            </div>
          )}
          {preview && !validating && (
            <div className="rounded-lg overflow-hidden fade-in" style={{ border: '1px solid var(--border-color)' }} data-testid="data-preview">
              <div className="px-3 py-2 flex items-center justify-between" style={{ background: 'var(--page-bg)', borderBottom: '1px solid var(--border-color)' }}>
                <span className="font-body" style={{ fontSize: 12, fontWeight: 600, color: 'var(--green-900)' }}>
                  Data Preview — {preview.total_rows} rows x {preview.total_cols} columns
                </span>
                <span className="font-body" style={{ fontSize: 11, color: 'var(--text-light)' }}>First 5 rows shown</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr style={{ background: '#F0F7F3' }}>
                      {preview.columns.map((col, i) => (
                        <th key={i} className="px-3 py-1.5 text-center font-body whitespace-nowrap" style={{ fontSize: 11, fontWeight: 600, color: 'var(--green-900)' }}>
                          {col}
                          <span className="block font-normal" style={{ fontSize: 9, color: 'var(--text-light)' }}>{preview.dtypes?.[col] || ''}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, ri) => (
                      <tr key={ri} style={{ background: ri % 2 === 0 ? 'white' : '#FAFAFA' }}>
                        {row.map((cell, ci) => (
                          <td key={ci} className={`px-3 py-1 text-center ${preview.dtypes?.[preview.columns[ci]] === 'numeric' ? 'font-mono' : 'font-body'}`} style={{ fontSize: 12, color: 'var(--text-dark)' }}>
                            {cell === '' || cell === null ? <span style={{ color: 'var(--text-light)' }}>—</span> : String(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sub-Batch 3R: Duplicate-upload banner (idempotency) */}
          <DuplicateBanner
            duplicateInfo={duplicateInfo}
            designLabel={design.label}
            onViewPrevious={() => { try { window.open(`/results/${duplicateInfo.duplicates[0].id}`, '_blank'); } catch {} }}
            onForceRerun={() => {
              setForceRerun(true);
              setDuplicateInfo(null);
              toast.info('Force re-run armed. Click Run Analysis to proceed.');
            }}
          />

          {/* Sub-Batch 3R: Upload progress + elapsed-time + cancel UI */}
          {uploading && (
            <div
              className="mb-3 rounded-lg border p-4"
              style={{ background: 'rgba(45, 106, 79, 0.06)', borderColor: 'var(--green-700)' }}
              data-testid="analysis-progress-panel"
            >
              {uploadProgress > 0 && uploadProgress < 100 ? (
                <>
                  <div className="flex justify-between text-xs font-semibold mb-2"
                       style={{ color: 'var(--green-900)' }}>
                    <span>Uploading file...</span>
                    <span data-testid="upload-progress-pct">{uploadProgress}%</span>
                  </div>
                  <div className="w-full h-2 rounded-full overflow-hidden bg-stone-200">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${uploadProgress}%`, background: 'var(--green-700)' }}
                      data-testid="upload-progress-bar"
                    />
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between" data-testid="analysis-running-row">
                  <div className="flex items-center gap-2 text-sm font-semibold"
                       style={{ color: 'var(--green-900)' }}>
                    <Loader2 size={16} className="animate-spin" />
                    <span data-testid="elapsed-counter">
                      Analysis in progress: {elapsedSec} second{elapsedSec === 1 ? '' : 's'} elapsed
                    </span>
                  </div>
                  <button
                    onClick={cancelAnalysis}
                    className="px-3 py-1 text-xs font-semibold rounded-md border-2"
                    style={{ borderColor: '#DC2626', color: '#991B1B', background: 'white' }}
                    data-testid="cancel-analysis-button"
                  >
                    Cancel
                  </button>
                </div>
              )}
              {elapsedSec >= 30 && elapsedSec < 90 && (
                <div className="mt-3 text-xs italic" style={{ color: 'var(--text-medium)' }}
                     data-testid="long-running-30s-note">
                  This analysis is taking longer than usual. You can wait, cancel, or check back later — your run will be saved.
                </div>
              )}
              {elapsedSec >= 90 && (
                <div className="mt-3 text-xs italic" style={{ color: 'var(--text-medium)' }}
                     data-testid="long-running-90s-note">
                  Analysis is still running.  You can leave this page; your analysis will continue and the result will be on your dashboard when you return.
                </div>
              )}
            </div>
          )}

          {/* Sub-Batch 3R: Structured failure banner */}
          {runError && (
            <div
              className="mb-3 rounded-lg border-2 p-4"
              style={{ background: '#FEF2F2', borderColor: '#DC2626' }}
              data-testid="structured-failure-banner"
            >
              <div className="flex items-start gap-3">
                <div className="text-2xl leading-none" aria-hidden="true">🚫</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm" style={{ color: '#991B1B' }}>
                    Failed at the <span style={{ textTransform: 'uppercase' }} data-testid="failure-stage">{runError.stage}</span> stage
                  </div>
                  <div className="text-xs mt-1 break-words" style={{ color: '#991B1B' }}
                       data-testid="failure-detail">
                    {runError.detail}
                  </div>
                  <div className="text-xs mt-2 italic" style={{ color: '#991B1B' }}
                       data-testid="failure-hint">
                    Next step: {runError.hint}
                  </div>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {/* Sub-Batch 3R-tail-3 — one-click contact-support
                        mailto with the failure context pre-filled, so
                        the user doesn't have to retype anything. */}
                    <a
                      href={`mailto:bkdataanalysis@bkagrid.com?subject=${encodeURIComponent(`BKAgriD analysis failed — ${design.label} — ${runError.stage}`)}&body=${encodeURIComponent(
                        `Hi BKAgriD team,\n\nMy ${design.label} analysis failed at the ${runError.stage.toUpperCase()} stage.\n\nDetail: ${runError.detail}\n\nFile: ${file?.name || '(no filename)'}\nTimestamp: ${new Date().toLocaleString()}\n\nCould you please run this analysis manually and share the result?\n\nThanks.`
                      )}`}
                      className="px-3 py-1.5 text-xs font-semibold rounded-md text-white inline-block no-underline"
                      style={{ background: '#DC2626' }}
                      data-testid="contact-support-mailto"
                    >
                      Email BKAgriD support
                    </a>
                    <button
                      onClick={() => setRunError(null)}
                      className="px-3 py-1.5 text-xs font-semibold rounded-md border"
                      style={{ borderColor: '#DC2626', color: '#991B1B', background: 'white' }}
                    >
                      Dismiss
                    </button>
                  </div>
                  {/* Sub-Batch 3X (Jun 2026) — opt-in: when Dr. Kumar
                      marks this failure as 'fixed' in /admin/failures,
                      the backend dispatches a Resend email to the user.
                      Only visible after the failure_id is captured
                      (i.e. the persistence POST succeeded). */}
                  {runError.failure_id && (
                    <label
                      className="flex items-center gap-2 mt-3 cursor-pointer select-none font-body"
                      style={{ fontSize: 12, color: '#991B1B' }}
                      data-testid="notify-when-fixed-label"
                    >
                      <input
                        type="checkbox"
                        defaultChecked={false}
                        className="accent-red-700"
                        data-testid="notify-when-fixed-checkbox"
                        onChange={async (e) => {
                          const v = e.target.checked;
                          try {
                            await axios.patch(
                              `${API}/uploads/failures/${runError.failure_id}/notify-preference`,
                              { notify_when_fixed: v },
                              { withCredentials: true },
                            );
                            toast.success(
                              v
                                ? "We'll email you when this failure is fixed"
                                : 'Notification preference cleared',
                              { duration: 3000 },
                            );
                          } catch {
                            toast.error('Could not save your preference', {
                              description: 'Please try again in a moment.',
                            });
                            e.target.checked = !v;
                          }
                        }}
                      />
                      <span>
                        Email me when BKAgriD fixes this (we&apos;ll let you know as soon as it&apos;s resolved)
                      </span>
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* L1g — Opt-in email-on-completion (Sub-Batch 3R-tail).
              Backend fires Resend only if elapsed > 90s, so the
              checkbox is safe to leave on for any analysis. */}
          <label
            className="flex items-center gap-2 mb-3 cursor-pointer select-none font-body"
            style={{ fontSize: 12, color: 'var(--text-medium)' }}
            data-testid="notify-on-completion-label"
          >
            <input
              type="checkbox"
              checked={notifyOnCompletion}
              onChange={(e) => setNotifyOnCompletion(e.target.checked)}
              className="accent-green-700"
              data-testid="notify-on-completion-checkbox"
              disabled={uploading}
            />
            <span>
              Email me a link when this completes (fires only if analysis runs &gt; 90 seconds)
            </span>
          </label>

          {/* Run Analysis Button */}
          <button
            onClick={handleUpload}
            disabled={!file || uploading || validating || validationErrors.length > 0 || (duplicateInfo && !forceRerun)}
            className="w-full font-body btn-press transition-all disabled:opacity-50"
            style={{
              height: 52,
              borderRadius: 'var(--radius-md)',
              fontWeight: 600, fontSize: 16, letterSpacing: '0.02em',
              color: 'white',
              background: 'linear-gradient(135deg, var(--green-700), var(--green-900))',
              border: 'none',
            }}
            data-testid={`analyze-${design.key}-button`}
          >
            {uploading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" /> Analyzing...
              </span>
            ) : `Run ${design.label} Analysis`}
          </button>
        </>
      )}

      {/* Export Bar */}
      <ExportBar
        analysisId={analysisId}
        color={design.color}
        exporting={exporting}
        onExport={onExport}
        designKey={design.key}
        onConnectPaper={connectToPaper}
      />

      {/* Field Layout & Efficiency Tools */}
      {analysisId && (
        <div className="flex items-center gap-2 flex-wrap pt-3" style={{ borderTop: '1px solid var(--border-color)' }} data-testid="design-tools-bar">
          <span className="font-body" style={{ fontSize: 10, fontWeight: 700, color: 'var(--green-900)', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 4 }}>Tools</span>
          <button onClick={() => setShowLayout(true)}
            className="flex items-center gap-1.5 font-body btn-press transition-colors"
            style={{
              fontSize: 11, fontWeight: 600, borderRadius: 'var(--radius-md)',
              padding: '5px 12px', border: '1px solid var(--green-900)',
              color: 'var(--green-900)', background: 'white',
            }}
            data-testid="generate-layout-btn">
            <Grid3X3 size={12} /> Generate Field Layout
          </button>
          <EfficiencyComparator analysisId={analysisId} designLabel={design.label} designColor={design.color} />
        </div>
      )}

      {showLayout && (
        <FieldLayoutModal
          analysisId={analysisId}
          designLabel={design.label}
          designColor={design.color}
          onClose={() => setShowLayout(false)}
        />
      )}

      <InsightPanel analysisId={analysisId} designLabel={design.label} color={design.color} />

      {/* Multi-trait Radar Chart */}
      {radarData && radarData.traits.length >= 3 && (
        <div className="mt-4">
          <MultiTraitRadar
            treatmentMeans={radarData.treatmentMeans}
            traits={radarData.traits}
          />
        </div>
      )}
    </div>
  );
}
